import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuthContext } from './AuthContext';
import {
  STORAGE_KEYS,
  STORAGE_VERSIONS,
  readStorageItem,
  readVersionedStorage,
  removeStorageItem,
  writeStorageItem,
  writeVersionedStorage,
} from '../utils/storage';

const FormProgressContext = createContext(null);

const normalizeUserId = (value) => {
  if (!value) return '';
  return String(value).trim();
};

const getFormProgressStorageKey = (userId) => {
  const activeUserId = normalizeUserId(userId);
  return activeUserId
    ? `${STORAGE_KEYS.FORM_PROGRESS}:${activeUserId}`
    : STORAGE_KEYS.FORM_PROGRESS;
};

const isOwnedByUser = (entry, userId) => {
  const activeUserId = normalizeUserId(userId);
  if (!activeUserId) return false;

  const ownerUserId = normalizeUserId(
    entry?.meta?.userId
    || entry?.meta?.ownerId
    || entry?.meta?.createdBy,
  );
  if (!ownerUserId) return false;

  return ownerUserId === activeUserId;
};

const filterOwnedForms = (forms, userId) => {
  if (!forms || typeof forms !== 'object' || Array.isArray(forms)) return {};
  return Object.entries(forms).reduce((acc, [id, entry]) => {
    if (isOwnedByUser(entry, userId)) {
      acc[id] = entry;
    }
    return acc;
  }, {});
};

const readStoredForms = (userId) => {
  const activeUserId = normalizeUserId(userId);
  if (!activeUserId || typeof window === 'undefined') return {};

  try {
    const scopedKey = getFormProgressStorageKey(activeUserId);
    const parsedScoped = readVersionedStorage(scopedKey, {
      fallback: {},
      expectedVersion: STORAGE_VERSIONS.FORM_PROGRESS,
    });
    const scopedForms = filterOwnedForms(
      parsedScoped && typeof parsedScoped === 'object' && !Array.isArray(parsedScoped) ? parsedScoped : {},
      activeUserId,
    );
    if (Object.keys(scopedForms).length > 0) {
      return scopedForms;
    }

    const legacyForms = filterOwnedForms(
      readVersionedStorage(STORAGE_KEYS.FORM_PROGRESS, {
        fallback: {},
        expectedVersion: STORAGE_VERSIONS.FORM_PROGRESS,
      }) || {},
      activeUserId,
    );
    if (Object.keys(legacyForms).length > 0) {
      writeVersionedStorage(scopedKey, legacyForms, {
        version: STORAGE_VERSIONS.FORM_PROGRESS,
      });
    }
    return legacyForms;
  } catch (error) {
    console.error('Failed to read stored form progress', error);
    return {};
  }
};

export const useFormProgressContext = () => {
  const ctx = useContext(FormProgressContext);
  if (!ctx) {
    throw new Error('useFormProgressContext must be used within a FormProgressProvider');
  }
  return ctx;
};

const readRecoveryFlag = () => {
  if (typeof window === 'undefined') return false;
  try {
    return readStorageItem(STORAGE_KEYS.FORM_RECOVERY_PENDING, { area: 'session' }) === 'true';
  } catch {
    return false;
  }
};

export const FormProgressProvider = ({ children }) => {
  const { user, sessionState } = useAuthContext();
  const activeUserId = useMemo(() => normalizeUserId(
    user?.id || user?._id || user?.userId || user?.bahaiId,
  ), [user]);
  const storageKey = useMemo(() => getFormProgressStorageKey(activeUserId), [activeUserId]);
  const [forms, setForms] = useState(() => readStoredForms(activeUserId));
  const [pendingRecovery, setPendingRecovery] = useState(() => readRecoveryFlag());
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    setForms(readStoredForms(activeUserId));
  }, [activeUserId]);

  useEffect(() => {
    if (!activeUserId || typeof window === 'undefined') return;
    if (typeof window !== 'undefined') {
      try {
        if (!forms || Object.keys(forms).length === 0) {
          removeStorageItem(storageKey);
        } else {
          writeVersionedStorage(storageKey, forms, {
            version: STORAGE_VERSIONS.FORM_PROGRESS,
          });
        }
      } catch (error) {
        console.error('Failed to persist form progress', error);
      }
    }
  }, [activeUserId, forms, storageKey]);

  useEffect(() => {
    if (sessionState?.status !== 'expired') return;
    if (typeof window !== 'undefined') {
      try {
        writeStorageItem(STORAGE_KEYS.FORM_RECOVERY_PENDING, 'true', { area: 'session' });
      } catch {
        // ignore
      }
    }
    setPendingRecovery(true);
  }, [sessionState?.status]);

  const saveFormState = useCallback((formId, data, meta = {}) => {
    if (!formId) return;
    if (!activeUserId) return;
    setForms((prev) => {
      const previous = prev?.[formId] || {};
      const next = {
        ...prev,
        [formId]: {
          data,
          meta: { ...previous.meta, ...meta },
          updatedAt: Date.now(),
        },
      };
      const entries = Object.entries(next)
        .map(([id, value]) => [id, value])
        .sort((a, b) => (b[1]?.updatedAt || 0) - (a[1]?.updatedAt || 0));
      const limited = entries.slice(0, 2).reduce((acc, [id, value]) => {
        acc[id] = value;
        return acc;
      }, {});
      return limited;
    });
    setRevision((prev) => prev + 1);
  }, [activeUserId]);

  const clearFormState = useCallback((formId) => {
    if (!formId) return;
    if (!activeUserId) return;
    let hasForms = false;
    setForms((prev) => {
      if (!prev || !prev[formId]) {
        hasForms = prev ? Object.keys(prev).length > 0 : false;
        return prev;
      }
      const next = { ...prev };
      delete next[formId];
      hasForms = Object.keys(next).length > 0;
      return next;
    });
    setRevision((prev) => prev + 1);
    if (!hasForms) {
      setPendingRecovery(false);
      if (typeof window !== 'undefined') {
        try {
          removeStorageItem(STORAGE_KEYS.FORM_RECOVERY_PENDING, { area: 'session' });
        } catch {
          // ignore
        }
      }
    }
  }, [activeUserId]);

  const clearAllForms = useCallback(() => {
    if (!activeUserId) return;
    setForms({});
    setRevision((prev) => prev + 1);
    setPendingRecovery(false);
    if (typeof window !== 'undefined') {
      try {
        removeStorageItem(storageKey);
        removeStorageItem(STORAGE_KEYS.FORM_RECOVERY_PENDING, { area: 'session' });
      } catch {
        // ignore
      }
    }
  }, [activeUserId, storageKey]);

  const loadFormState = useCallback((formId) => {
    return forms?.[formId]?.data ?? null;
  }, [forms]);

  const getFormEntry = useCallback((formId) => forms?.[formId] || null, [forms]);

  const listSavedForms = useCallback(() => {
    if (!forms) return [];
    return Object.entries(forms)
      .map(([id, entry]) => ({ id, ...entry }))
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  }, [forms]);

  const acknowledgeRecovery = useCallback(() => {
    setPendingRecovery(false);
    if (typeof window !== 'undefined') {
      try {
        writeStorageItem(STORAGE_KEYS.FORM_RECOVERY_PENDING, 'false', { area: 'session' });
      } catch {
        // ignore
      }
    }
  }, []);

  const triggerRecoveryPrompt = useCallback(() => {
    if (!forms || Object.keys(forms).length === 0) {
      return;
    }
    setPendingRecovery(true);
    if (typeof window !== 'undefined') {
      try {
        writeStorageItem(STORAGE_KEYS.FORM_RECOVERY_PENDING, 'true', { area: 'session' });
      } catch {
        // ignore
      }
    }
  }, [forms]);

  const value = useMemo(() => ({
    saveFormState,
    clearFormState,
    loadFormState,
    getFormEntry,
    listSavedForms,
    pendingRecovery,
    setPendingRecovery,
    formsRevision: revision,
    acknowledgeRecovery,
    triggerRecoveryPrompt,
    clearAllForms,
  }), [
    saveFormState,
    clearFormState,
    loadFormState,
    getFormEntry,
    listSavedForms,
    pendingRecovery,
    revision,
    acknowledgeRecovery,
    triggerRecoveryPrompt,
    clearAllForms,
  ]);

  return (
    <FormProgressContext.Provider value={value}>
      {children}
    </FormProgressContext.Provider>
  );
};
